
import { Component, Prop, Emit, Watch, Vue } from 'vue-property-decorator';
import DateTimePicker from '@/components/DateTimePicker.vue';
import Util from '@/utils/util';
import imageConvert from '@/utils/image';
import adBlockType from '@/enums/adBlockType';

@Component({
  components: { DateTimePicker },
})
export default class AppAdvertisementForm extends Vue {
  @Prop({}) private title!: string;

  @Prop({}) private openLinkMethod!: number;

  @Prop({}) private startTime!: string;

  @Prop({}) private endTime!: string;

  @Prop({}) private imageUrl!: string;

  @Prop({}) private url!: string | null;

  @Prop({}) private priority!: number;

  @Prop({ default: false }) private loading!: boolean;

  @Prop({}) private blockType!: adBlockType;

  private valid: boolean = false;

  private imagePic: any = '';

  private get imageSizeHint() {
    let hint = '';
    switch (this.blockType) {
      case adBlockType.Popup:
        hint = '1200*1200';
        break;
      case adBlockType.Carousel:
        hint = '1920*483';
        break;
      default:
        break;
    }
    return hint;
  }

  private form = {
    name: this.title,
    openLinkMethod: this.openLinkMethod,
    startTime: this.startTime,
    endTime: this.endTime,
    imageUrl: this.imageUrl,
    imagePic: this.imageUrl,
    url: this.url,
    priority: this.priority,
  };

  private rules = {
    title: [this.requiredRule('廣告標題'), this.maxLengthRule('廣告標題', 200)],
    url: [this.maxLengthRule('廣告連結', 1000)],
    openLinkMethod: [this.validateOpenLinkMethod()],
    startTime: [
      this.requiredRule('上架時間'),
      this.lessThenRule(
        '上架時間',
        '上架時間',
        (vm: AppAdvertisementForm) => vm.form.endTime,
      ),
    ],
    endTime: [
      this.requiredRule('下架時間'),
      this.greaterThenRule(
        '下架時間',
        '下架時間',
        (vm: AppAdvertisementForm) => vm.form.startTime,
      ),
    ],
    imageUrl: [this.validatePic((vm: AppAdvertisementForm) => vm.imagePic)],
    priority: [this.requiredRule('排序')],
  };

  private get inputs() {
    return {
      title: this.title,
      openLinkMethod: this.openLinkMethod,
      startTime: this.startTime,
      endTime: this.endTime,
      imageUrl: this.imageUrl,
      url: this.url,
      priority: this.priority,
    };
  }

  private get datePeriod() {
    return {
      startOn: this.form.startTime,
      endOn: this.form.endTime,
    };
  }

  @Watch('inputs', { immediate: true })
  private inputsWatcher(val: any) {
    this.form = {
      ...val,
    };
    this.imagePic = this.imageUrl;
  }

  @Watch('datePeriod')
  private datePeriodWatcher(val: any) {
    const inputs: any[] = (this.$refs.form as any).inputs.filter(
      (i: any) => i.label === '上架時間' || i.label === '下架時間',
    );
    inputs.forEach((input) => {
      input.validate(true);
    });
  }

  private requiredRule(column: string) {
    return (v: any) => !Util.isNullOrEmpty(v) || `${column} 為必填`;
  }

  private maxLengthRule(column: string, length: number) {
    return (v: any) => !v || v?.length <= length || `${column} 最多 ${length} 字`;
  }

  private lessThenRule(
    column: string,
    column2: string,
    func: (vm: AppAdvertisementForm) => any,
  ) {
    return (v: any) => {
      const c = func(this);
      return !v || !c || v <= c || `${column}要小於${column2}`;
    };
  }

  private greaterThenRule(
    column: string,
    column2: string,
    func: (vm: AppAdvertisementForm) => any,
  ) {
    return (v: any) => {
      const c = func(this);
      return !v || !c || v >= c || `${column}要大於${column2}`;
    };
  }

  private validateOpenLinkMethod() {
    return (v: number | null) => !this.form.url || !!v || '請選擇連結開啟方式';
  }

  private validatePic(func: (vm: AppAdvertisementForm) => any) {
    return (v: any) => {
      const p = func(this);
      return !!v || !!p || '請上傳圖片';
    };
  }

  private handleClearImage() {
    this.form.imageUrl = '';
    this.imagePic = '';
  }

  private changeImage(event: any) {
    const imagePic = URL.createObjectURL(event);
    this.form.imageUrl = event;
    this.imagePic = imagePic;
  }

  private async cancel() {
    await this.$router.back();
  }

  @Emit()
  private async submit() {
    const form = this.$refs.form as any as { validate: () => boolean };
    if (form.validate()) {
      const openLinkMethod = this.form.url ? this.form.openLinkMethod : 0;
      return {
        ...this.form,
        openLinkMethod,
        imageUrl: await imageConvert(this.form.imageUrl as any),
      };
    }
    return null;
  }
}
